import PathAggregation from '@/reports/path/path.aggregation';
import PathCsvAggregation from '@/reports/path/path-csv.aggregation';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { AI_AGENT_ICON_TYPE } from '@/ai-agents/components/ai-agents.constants';
import get from 'lodash/get';
import { getPathTree, getVisitorPaths, processPaths } from './traversal';
import { asyncMutateResponse, connectAggregationToObservable } from '@pendo/agg-connect';
import { ReportError, getDefaultTimeseries, buildReportStream, isStale, getMobileProperties } from '@/reports/utils';
import { isWebBasedApp } from '@/utils/apps';
import { of } from 'rxjs';
import { parseSegmentParams } from '@/utils/segments';
import { FilterType } from '@/types/analytics/analytics.types';
import {
    fetchAiAgents,
    fetchAiAgent,
} from '@/ai-agents/components/ai-agents.utils';
import { createFrozenMap } from '@/vuex/utils';
import { getActiveApplicationsMap } from '@/data/session/session-globals.ts';
import { VanillaDateRange } from '@/data/vanilla-date-range';
import { isPlatformApp } from '@/apps/apps.utils';

function getSubId () {
    return store.state.subscription.active.id;
}

export const PATH_PARAMETERS_KEY = '_pendo_path_parameters';

export function buildPathSupportingAppsMap (integratedApps) {
    return integratedApps.reduce((map, app) => {
        const isEnabled = isWebBasedApp(app.platform) || app.applicationFlags.mobileRetroactiveAnalyticsApp === true;
        map[app.id] = isEnabled;

        return map;
    }, {});
}

export function buildAggParams (path) {
    const { definition } = path;
    const { config } = definition;
    config.fromWebAnalytics = path.fromWebAnalytics;
    const vanillaDateRange = new VanillaDateRange(definition.timeSeries);
    const timeSeries = vanillaDateRange.timeSeries();

    return {
        config: parseSegmentParams(config),
        timeSeries,
    };
}

export function compileAggregation (path) {
    const params = buildAggParams(path);

    return PathAggregation.build(params);
}

export async function retrieveAgentsData () {
    try {
        const unformattedAgents = await fetchAiAgents();
        const aiAgents = unformattedAgents.map((agent) => ({
            ...agent,
            icon: {
                type: AI_AGENT_ICON_TYPE,
            },
            isStarred: false,
            group: {
                id: '',
                name: '',
                description: '',
                color: '',
                length: 0,
                items: null,
                type: '',
            },
        }));
        const aiAgentsMap = createFrozenMap(aiAgents);
        const recentAgentEvents = store.getters['userSettings/history/getRecentAgentEvents'](aiAgentsMap);

        return {
            aiAgents,
            aiAgentsMap,
            recentAgentEvents,
        };
    } catch {
        return {
            aiAgents: [],
            aiAgentsMap: {},
            recentAgentEvents: [],
        };
    }
}

async function rootResourceExists (path) {
    try {
        let rootResource;

        const { definition: { config: { agentId } = {} } = {} } = path;
        if (agentId) {
            const resource = await fetchAiAgent(agentId);
            rootResource = resource;
        } else {
            await store.dispatch('savedReports/paths/loadRootResource', {
                id: path.id,
            });
            rootResource = store.getters['savedReports/paths/rootResourceByReportId'](path.id);
        }

        return !!rootResource;
    } catch {
        return false;
    }
}

async function validate (path) {
    if (isLongReversePathOnBlacklistedSub(path, getSubId())) {
        throw new ReportError({
            message:
                'Reverse-direction paths are temporarily disabled for date ranges spanning more than 30 days while we work to improve their support.',
            retriable: false,
        });
    }

    const runnable = await rootResourceExists(path);
    if (!runnable) {
        throw new ReportError({
            message:
                'We couldn’t find the Page, Feature, or Track Event you specified. Consider updating the activity source or deleting the path if you no longer need it.',
            retriable: false,
        });
    }
}

function processResponse (path, rows) {
    const rootNode = getPathTree(rows) || {};
    rootNode.predecessors = get(path, 'definition.config.predecessors');
    const paths = getVisitorPaths(rows, rootNode.mapping);

    processPaths(rootNode);

    return {
        root: rootNode,
        paths,
    };
}

export function createPathStream (labelerPromise, isWidget = false) {
    return buildReportStream(
        {
            aggregation: asyncMutateResponse(PathAggregation, async (response) => {
                await labelerPromise;

                return response;
            }),
            validate,
            buildAggParams,
            processResponse,
        },
        isWidget,
    );
}

export function isLongReversePathOnBlacklistedSub (path, subId) {
    const isReverseTime = get(path, 'definition.config.predecessors', false);
    const tooManyDaysInTimeSeries = getNumDaysInTimeSeries(path) > 30;
    const isBlacklistedSub = ['6302733875544064', '5077612317114368'].includes(subId);

    return isBlacklistedSub && isReverseTime && tooManyDaysInTimeSeries;
}

function getNumDaysInTimeSeries (path) {
    const timeSeries = get(path, 'definition.timeSeries');
    const vanillaDateRange = new VanillaDateRange(timeSeries);
    const ts = vanillaDateRange.timeSeries();

    return ts.count;
}

export function getPathEventApp (eventType, eventId) {
    const getterFunction = eventType === 'guide' ? 'getGuideById' : `${eventType}ById`;
    const event = store.getters[`${eventType}s/${getterFunction}`](eventId);

    const appMap = getActiveApplicationsMap();

    return appMap[event.appId];
}

export function getDefaultUnsavedPath (setReplayData, hasAiAgentsSegmentFlag) {
    return {
        name: 'New Path',
        shared: true,
        type: 'Path',
        definition: {
            type: 'Path', // keeping for backwards compat with old paths code
            config: {
                maxInterval: 3600,
                predecessors: false,
                omitPages: false,
                features: true,
                trackEvents: true,
                guideEvents: true,
                agentEvents: hasAiAgentsSegmentFlag,
                collapseDups: true,
                uniqueVisitors: false,
                maxLength: 10,
                segmentId: 'everyone',
                acrossApps: false,
                replayData: setReplayData,
            },
            minimum: 0,
            timeSeries: getDefaultTimeseries(),
            history: [],
        },
    };
}

export function fetchCsv (path) {
    const { predecessors, acrossApps } = path.definition.config;

    return connectAggregationToObservable(
        PathCsvAggregation,
        of({
            gcsFileName: path.lastSuccessRunObj,
            acrossApps,
            predecessors,
        }),
    );
}

export function trackPathReport ({
    pathResource,
    trackType,
    location = 'report',
    isAuto = false,
    fromWebAnalytics = false,
    fromAiAgents = false,
}) {
    const { definition, id } = pathResource;
    const { config, timeSeries } = definition;
    const {
        maxLength,
        acrossApps,
        collapseDups,
        uniqueVisitors,
        segmentId,
        predecessors,
        pageId,
        featureId,
        trackTypeId,
        guideId,
        agentId,
        excludedEvents,
        omitPages,
        features,
        trackEvents,
        guideEvents,
        agentEvents,
        reportFilters,
        eventFilters,
        appId,
        topic,
    } = config;

    let anchorEventType;
    if (pageId) anchorEventType = 'page';
    if (featureId) anchorEventType = 'feature';
    if (trackTypeId) anchorEventType = 'track event';
    if (guideId) anchorEventType = 'guide';
    if (agentId) anchorEventType = 'ai agent';

    const excludedEventTypes = [];
    const excludedEventList = excludedEvents || [];

    let numOfExcludedPages = 0;
    let numOfExcludedFeatures = 0;
    let numOfExcludedTrackEvents = 0;
    let numOfExcludedGuides = 0;
    let numOfExcludedAgents = 0;

    for (const event of excludedEventList) {
        switch (event.kind) {
            case 'Page':
                numOfExcludedPages++;
                break;
            case 'Feature':
                numOfExcludedFeatures++;
                break;
            case 'TrackType':
                numOfExcludedTrackEvents++;
                break;
            case 'Guide':
                numOfExcludedGuides++;
                break;
            case 'AIAgent':
                numOfExcludedAgents++;
                break;
        }
    }

    if (numOfExcludedPages > 0) excludedEventTypes.push('pages');
    if (numOfExcludedFeatures > 0) excludedEventTypes.push('features');
    if (numOfExcludedTrackEvents > 0) excludedEventTypes.push('track events');
    if (numOfExcludedGuides > 0) excludedEventTypes.push('guides');
    if (numOfExcludedAgents > 0) excludedEventTypes.push('ai agent');

    const includedEventTypes = [];
    if (!omitPages) includedEventTypes.push('pages');
    if (features) includedEventTypes.push('features');
    if (trackEvents) includedEventTypes.push('track events');
    if (guideEvents) includedEventTypes.push('guides');
    if (agentEvents) includedEventTypes.push('ai agent');

    const trackMobileProperties = getMobileProperties([{ appId }]);

    const allFilters = [...(eventFilters || []), ...(reportFilters || [])];

    const hasSessionProperties = allFilters.some((obj) => obj.filterType === FilterType.Utm);

    const pathTrackEventData = {
        reportId: id,
        location,
        trackType,
        dateRange: timeSeries.type,
        segment: segmentId === 'everyone' ? 'default' : 'custom',
        showPaths: predecessors ? 'leadingTo' : 'startingFrom',
        anchorEventType,
        across_apps: acrossApps,
        collapse_repeating_steps: collapseDups,
        remove_duplicate_visitor_paths: uniqueVisitors,
        maximum_path_length: maxLength,
        pages: !omitPages,
        features,
        trackEvents,
        guides: guideEvents,
        agentEvents,
        included_eventTypes: includedEventTypes,
        excluded_pages: numOfExcludedPages > 0,
        excluded_features: numOfExcludedFeatures > 0,
        excluded_trackEvents: numOfExcludedTrackEvents > 0,
        excluded_guides: numOfExcludedGuides > 0,
        excluded_ai_agents: numOfExcludedAgents > 0,
        number_of_excluded_pages: numOfExcludedPages,
        number_of_excluded_features: numOfExcludedFeatures,
        number_of_excluded_trackEvents: numOfExcludedTrackEvents,
        number_of_excluded_guides: numOfExcludedGuides,
        number_of_excluded_ai_agents: numOfExcludedAgents,
        excluded_eventTypes: excludedEventTypes,
        has_excluded_events: excludedEventTypes.length > 0,
        has_event_filters: eventFilters?.length > 0,
        has_report_filters: reportFilters?.length > 0,
        isAuto,
        isStale: isAuto && trackType !== 'run' ? isStale(pathResource) : false,
        ...trackMobileProperties,
        has_session_properties: hasSessionProperties,
        from_web_analytics: fromWebAnalytics,
        from_ai_agents: fromAiAgents,
        semantic_search_prompt: topic || '',
    };

    window.pendo.track('Run Path Report', pathTrackEventData);
}

export function getSelectedEvents (visitorPath, depth) {
    return visitorPath.slice(0, depth + 1).reduce(
        (acc, step) => {
            if (get(step, 'items[0].featureId')) {
                acc.feature.push(step.items[0].featureId);
            } else if (get(step, 'items[0].guideId')) {
                acc.guide.push(step.items[0].guideId);
            } else if (get(step, 'items[0].pageId')) {
                acc.page.push(step.items[0].pageId);
            }

            return acc;
        },
        { page: [], feature: [], guide: [] },
    );
}

export function calculateAllAppsForPath ({ mappedApps, anchorEventAppId, acrossApps, acrossAppIds }) {
    const formattedApps = mappedApps;

    if (!anchorEventAppId) return formattedApps;

    const isAcrossDefaultAllApps = acrossApps && acrossAppIds.length === 0;
    const resolvedAcrossAppIds = isAcrossDefaultAllApps ? ['*'] : acrossAppIds;
    const isAcrossPlatformApp = acrossApps && resolvedAcrossAppIds.length === 1 && isPlatformApp(resolvedAcrossAppIds[0]);

    if (isAcrossPlatformApp) {
        if (resolvedAcrossAppIds[0] === '*') {
            return formattedApps;
        }

        return formattedApps.filter(({ platform, id }) => {
            if (resolvedAcrossAppIds[0] === 'mobile') {
                return ['ios', 'android', 'mobile'].includes(platform) || id === anchorEventAppId;
            }

            return platform === resolvedAcrossAppIds[0] || id === anchorEventAppId;
        });
    }

    const allIncludedAppsIds = [...resolvedAcrossAppIds, anchorEventAppId];
    const appFilteredApps = acrossApps
        ? formattedApps.filter(({ id }) => allIncludedAppsIds.includes(id))
        : formattedApps.filter(({ id }) => id === anchorEventAppId);

    return appFilteredApps;
}

export function migratePathConfig (mappedApps, path) {
    // Guard: Don't migrate if apps aren't loaded yet, as mappedApps could be empty
    if (!mappedApps || mappedApps.length === 0) {
        return { path, expandedAppIdsWasSet: false };
    }

    const { config } = path.definition;
    const { appId, acrossApps, selectedAppIds } = config;

    const hasSelectedAppIds = config.selectedAppIds?.length;
    const hasExpandedAppIds = config.expandedAppIds !== undefined;

    const allAppsNeedExpansion = acrossApps && !hasSelectedAppIds && !hasExpandedAppIds;
    if (allAppsNeedExpansion) {
        const expandedAppIds = calculateAllAppsForPath({ mappedApps, anchorEventAppId: appId, acrossApps, acrossAppIds: [] });
        config.expandedAppIds = expandedAppIds.map(({ id }) => id);
        config.selectedAppIds = ['*'];

        return { path, expandedAppIdsWasSet: true };
    }

    const appIdsNeedExpansion = acrossApps && hasSelectedAppIds && !hasExpandedAppIds;
    if (appIdsNeedExpansion) {
        const expandedAppIds = calculateAllAppsForPath({ mappedApps, anchorEventAppId: appId, acrossApps, acrossAppIds: selectedAppIds });
        config.expandedAppIds = expandedAppIds.map(({ id }) => id);

        return { path, expandedAppIdsWasSet: true };
    }

    return { path, expandedAppIdsWasSet: false };
}